import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import type { UploadFilter, UploadSessionStatus } from "../types";
import { filters as filterCopy, countLabels } from "../copy";
import IntakeClock from "./IntakeClock";

interface Counts {
  accepted: number;
  errors: number;
  pending: number;
  total: number;
}

interface StatusStripProps {
  counts: Counts;
  activeFilter: UploadFilter;
  onFilterChange: (f: UploadFilter) => void;
  sessionStartedAt: number | null;
  sessionStatus: UploadSessionStatus | null;
  hasError?: boolean;
}

interface FilterChipDef {
  key: UploadFilter;
  label: string;
  count: number;
  activeFill: string;
  activeText: string;
}

const FILTER_CHIPS: Omit<FilterChipDef, "count">[] = [
  { key: "ALL", label: filterCopy.all, activeFill: "rgba(0,0,0,0.06)", activeText: "#0B1F15" },
  { key: "ERRORS", label: filterCopy.errors, activeFill: "rgba(211,47,47,0.10)", activeText: "#D32F2F" },
  { key: "PENDING", label: filterCopy.pending, activeFill: "rgba(124,77,255,0.10)", activeText: "#5E35B1" },
  { key: "ACCEPTED", label: filterCopy.accepted, activeFill: "rgba(38,106,74,0.12)", activeText: "#266A4A" },
];

const MetricPill: React.FC<{
  glyph?: string;
  glyphColor?: string;
  count: number;
  label: string;
  countColor?: string;
}> = ({ glyph, glyphColor, count, label, countColor = "#0B1F15" }) => (
  <Stack direction="row" alignItems="center" spacing={0.5}>
    {glyph && (
      <Typography component="span" sx={{ color: glyphColor, fontSize: "14px", lineHeight: 1 }}>
        {glyph}
      </Typography>
    )}
    <Typography
      component="span"
      sx={{
        fontFeatureSettings: '"tnum"',
        fontSize: "13px",
        fontWeight: 600,
        color: countColor,
        fontFamily: "Poppins, sans-serif",
      }}
    >
      {count}
    </Typography>
    <Typography
      component="span"
      sx={{ fontSize: "13px", fontWeight: 400, color: "#5B6B61", fontFamily: "Poppins, sans-serif" }}
    >
      {label}
    </Typography>
  </Stack>
);

const StatusStrip: React.FC<StatusStripProps> = ({
  counts,
  activeFilter,
  onFilterChange,
  sessionStartedAt,
  sessionStatus,
  hasError,
}) => {
  const chipCounts: Record<UploadFilter, number> = {
    ALL: counts.total,
    ERRORS: counts.errors,
    PENDING: counts.pending,
    ACCEPTED: counts.accepted,
  };

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 2,
        height: "56px",
        background: "rgba(250,252,248,0.92)",
        backdropFilter: "blur(8px)",
        borderBottom: "1px solid rgba(0,0,0,0.06)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 2,
        gap: 2,
      }}
    >
      {/* Left — counts */}
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <MetricPill glyph="✔" glyphColor="#266A4A" count={counts.accepted} label={countLabels.accepted} countColor="#0B1F15" />
        <Box sx={{ width: "1px", height: "14px", background: "rgba(0,0,0,0.12)" }} />
        <MetricPill glyph="✕" glyphColor="#D32F2F" count={counts.errors} label={countLabels.errors} countColor="#0B1F15" />
        <Box sx={{ width: "1px", height: "14px", background: "rgba(0,0,0,0.12)" }} />
        <MetricPill glyph="⊙" glyphColor="rgba(0,0,0,0.45)" count={counts.pending} label={countLabels.pending} countColor="#0B1F15" />
        <Box sx={{ width: "1px", height: "14px", background: "rgba(0,0,0,0.12)" }} />
        <MetricPill count={counts.total} label={countLabels.total} countColor="#5B6B61" />
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1.5}>
        {/* Filter chips */}
        <Stack direction="row" spacing={0.75}>
          {FILTER_CHIPS.map((chip) => {
            const count = chipCounts[chip.key];
            const isActive = activeFilter === chip.key;
            const isDisabled = chip.key !== "ALL" && count === 0;
            return (
              <Box
                key={chip.key}
                component="button"
                onClick={() => !isDisabled && onFilterChange(chip.key)}
                disabled={isDisabled}
                aria-pressed={isActive}
                sx={{
                  height: "28px",
                  borderRadius: "24px",
                  padding: "0 14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "13px",
                  fontWeight: 500,
                  cursor: isDisabled ? "default" : "pointer",
                  opacity: isDisabled ? 0.45 : 1,
                  border: isActive ? "none" : "1px solid rgba(0,0,0,0.12)",
                  background: isActive ? chip.activeFill : "transparent",
                  color: isActive ? chip.activeText : "#5B6B61",
                  transition: "all 200ms ease-out",
                  "&:hover:not(:disabled)": !isActive
                    ? { background: "rgba(0,0,0,0.04)" }
                    : {},
                }}
              >
                {chip.label}
                {count > 0 && (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: isActive ? chip.activeText : "#5B6B61",
                      ml: "2px",
                    }}
                  >
                    {count}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>

        {/* Intake clock */}
        <IntakeClock startedAt={sessionStartedAt} sessionStatus={sessionStatus} stopped={hasError} />
      </Stack>
    </Box>
  );
};

export default StatusStrip;
